import React, { useState, useContext, useEffect, useRef } from "react";
import dayjs from "dayjs";
import "./exclusionModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import { Trash2 } from "lucide-react";
import scheduleBasedRequest from "../../../request/scheduleBasedRequest";
import translations from "../../../translations/translations";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import BannerMessage from "../../common/bannerMessage/bannerMessage";
import BannerMessageFixed from "../../common/bannerMessageFixed/bannerMessageFixed";
import StaffDropdown from "../../common/StaffDropdown/StaffDropdown";

const ExclusionModal = ({ onClose, onSaved, staffList = [], initialStaffId, token, scheduleData = [], onManageBooking }) => {
	const { userData, language } = useContext(CombinedContext);

	const [selectedStaff, setSelectedStaff] = useState(null);
	const [selectedScheduleId, setSelectedScheduleId] = useState(null);

	const [startDate, setStartDate] = useState(dayjs().format("YYYY-MM-DD"));
	const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
	const [fullDay, setFullDay] = useState(true);
	const [startTime, setStartTime] = useState("09:00");
	const [endTime, setEndTime] = useState("17:00");
	const [reason, setReason] = useState("");
	const [saving, setSaving] = useState(false);
	const [toast, setToast] = useState("");
	const [showStartCalendar, setShowStartCalendar] = useState(false);
	const [showEndCalendar, setShowEndCalendar] = useState(false);
	const [bookingsByDate, setBookingsByDate] = useState({});
	const [bookingConflicts, setBookingConflicts] = useState([]);
	const [banner, setBanner] = useState({ message: "", type: "error" });
	const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
	const [exclusions, setExclusions] = useState([]);
	const [isDeleting, setIsDeleting] = useState(false);

	const hasInitialized = useRef(false);

	useEffect(() => {
		if (staffList.length > 0 && !hasInitialized.current) {
			hasInitialized.current = true;
			const target = initialStaffId ? staffList.find((s) => s.staffId === initialStaffId) : staffList[0];
			const first = target || staffList[0];
			setSelectedStaff(first);
			setSelectedScheduleId(first.scheduleId);
		}
	}, [staffList]);

	useEffect(() => {
		if (!selectedStaff || !scheduleData) return;

		const entry = scheduleData.find((s) => s.staffId === selectedStaff.staffId);

		setExclusions(entry?.exclusions || []);
		setBookingsByDate(entry?.bookingsByDate || {});
		setBookingConflicts([]);
	}, [selectedStaff, scheduleData]);

	useEffect(() => {
		function handleClick(e) {
			if (!e.target.closest(".sf-calendar-popover") && !e.target.closest(".sf-date-trigger")) {
				setShowStartCalendar(false);
				setShowEndCalendar(false);
			}
		}

		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, []);

	const showBanner = (message, type = "error") => {
		setBanner({ message, type });
	};

	const clearBanner = () => setBanner({ message: "", type: "error" });

	function getBlockedRangesForDate(dateStr) {
		return exclusions
			.filter((ex) => {
				const dateObj = dayjs(dateStr);
				const exStart = dayjs(ex.startDate);
				const exEnd = dayjs(ex.endDate);

				return !dateObj.isBefore(exStart) && !dateObj.isAfter(exEnd);
			})
			.flatMap((ex) => {
				if (!ex.blocks || ex.blocks.length === 0) {
					return [{ start: "00:00", end: "23:59" }];
				}
				return ex.blocks;
			});
	}

	function timeConflicts(dateStr, start, end) {
		if (!start || !end) return false;

		const blockedRanges = getBlockedRangesForDate(dateStr);

		return blockedRanges.some((b) => {
			return start < b.end && b.start < end;
		});
	}

	const isSingleDay = startDate === endDate;

	const times = Array.from({ length: 48 }, (_, i) => {
		const h = Math.floor(i / 2);
		const m = i % 2 === 0 ? "00" : "30";
		return `${String(h).padStart(2, "0")}:${m}`;
	});

	function isPast(date) {
		return dayjs(date).isBefore(dayjs(), "day");
	}

	function isFullyBlocked(date) {
		return exclusions.some((ex) => {
			const d = dayjs(date);
			const exStart = dayjs(ex.startDate);
			const exEnd = dayjs(ex.endDate);

			const inRange = !d.isBefore(exStart) && !d.isAfter(exEnd);

			const isFull = !ex.blocks || ex.blocks.length === 0 || (ex.blocks[0]?.start === "00:00" && ex.blocks[0]?.end === "23:59");

			return inRange && isFull;
		});
	}

	function isPartiallyBlocked(date) {
		return exclusions.some((ex) => {
			const d = dayjs(date);
			const exStart = dayjs(ex.startDate);
			const exEnd = dayjs(ex.endDate);

			const inRange = !d.isBefore(exStart) && !d.isAfter(exEnd);

			if (!inRange) return false;

			// Has blocks but not full-day
			return ex.blocks && ex.blocks.length > 0 && !(ex.blocks.length === 1 && ex.blocks[0].start === "00:00" && ex.blocks[0].end === "23:59");
		});
	}

	function doesOverlap(newExclusion) {
		return exclusions.some((ex) => {
			const newStart = dayjs(newExclusion.startDate);
			const newEnd = dayjs(newExclusion.endDate);

			const exStart = dayjs(ex.startDate);
			const exEnd = dayjs(ex.endDate);

			// 1️⃣ Date range overlap
			const datesOverlap = !newEnd.isBefore(exStart) && !newStart.isAfter(exEnd);

			if (!datesOverlap) return false;

			const newIsFull = newExclusion.blocks.length === 0;
			const exIsFull = ex.blocks?.length === 0;

			// 2️⃣ If either is full day → overlap
			if (newIsFull || exIsFull) return true;

			// 3️⃣ Check time block overlap
			return newExclusion.blocks.some((nb) =>
				ex.blocks.some((eb) => {
					const newStartTime = dayjs(nb.start, "HH:mm");
					const newEndTime = dayjs(nb.end, "HH:mm");
					const exStartTime = dayjs(eb.start, "HH:mm");
					const exEndTime = dayjs(eb.end, "HH:mm");

					return newStartTime.isBefore(exEndTime) && exStartTime.isBefore(newEndTime);
				}),
			);
		});
	}

	function getBookingsForDate(dateStr) {
		return bookingsByDate?.[dateStr] || [];
	}

	function rangeOverlaps(aStart, aEnd, bStart, bEnd) {
		// assumes "HH:mm" for time strings
		return aStart < bEnd && bStart < aEnd;
	}

	function collectBookingConflicts(newExclusion) {
		const conflicts = [];

		const startD = dayjs(newExclusion.startDate);
		const endD = dayjs(newExclusion.endDate);

		// Walk each day in the date range
		for (let d = startD; !d.isAfter(endD, "day"); d = d.add(1, "day")) {
			const dateStr = d.format("YYYY-MM-DD");
			const bookings = getBookingsForDate(dateStr);

			if (!bookings.length) continue;

			// full day exclusion conflicts with ANY booking that day
			const isFullDay = !newExclusion.blocks || newExclusion.blocks.length === 0;

			if (isFullDay) {
				bookings.forEach((b) => conflicts.push(b));
				continue;
			}

			// partial day (your UI only allows partial for single day)
			const block = newExclusion.blocks[0];
			if (!block?.start || !block?.end) continue;

			// booking start/end are ISO, convert to HH:mm for comparison
			bookings.forEach((b) => {
				const bStart = dayjs(b.start).format("HH:mm");
				const bEnd = dayjs(b.end).format("HH:mm");

				if (rangeOverlaps(block.start, block.end, bStart, bEnd)) {
					conflicts.push(b);
				}
			});
		}

		// de-dupe by bookingId just in case
		const seen = new Set();
		return conflicts.filter((c) => {
			if (!c.bookingId) return true;
			if (seen.has(c.bookingId)) return false;
			seen.add(c.bookingId);
			return true;
		});
	}

	async function saveExclusion() {
		if (!selectedScheduleId) return;

		const t = translations[language].calendar.exclusion;

		const newExclusion = {
			startDate,
			endDate,
			reason: reason || t.reasonDefault,
			blocks: fullDay ? [] : [{ start: startTime, end: endTime }],
		};

		// ⛔ Basic validation
		if (!fullDay) {
			if (!startTime || !endTime) {
				showBanner(language === "es" ? "Selecciona hora de inicio y fin" : "Please select both start and end time", "error");
				return;
			}

			if (endTime <= startTime) {
				showBanner(language === "es" ? "La hora de fin debe ser después de la hora de inicio" : "End time must be after start time", "error");
				return;
			}
		}

		const conflicts = collectBookingConflicts(newExclusion);

		if (conflicts.length > 0) {
			setBookingConflicts(conflicts);

			showBanner(t.conflictsBanner.replace("{count}", conflicts.length), "error");
			return;
		}

		setBookingConflicts([]);

		if (doesOverlap(newExclusion)) {
			showBanner(t.overlapBanner, "error");
			return;
		}

		try {
			setSaving(true);

			await scheduleBasedRequest.addScheduleExclusion(selectedScheduleId, newExclusion, token);

			if (onSaved) await onSaved();

			showBanner(t.toast.saved, "success");

			setTimeout(() => {
				clearBanner();
				onClose();
			}, 800);
		} catch (err) {
			console.error("Error saving exclusion:", err);
			showBanner(t.toast.error, "error");
		} finally {
			setSaving(false);
		}
	}

	const handleDelete = async (index) => {
		if (!selectedScheduleId) return;

		setIsDeleting(true);
		setExclusions((prev) => prev.filter((_, i) => i !== index));

		try {
			await scheduleBasedRequest.deleteScheduleExclusion(selectedScheduleId, index, token);
			if (onSaved) await onSaved();
			setToast(translations[language].calendar.exclusion.toast.removed);
			setTimeout(() => setToast(""), 800);
		} catch (err) {
			console.error("Failed to delete exclusion:", err);
			const entry = scheduleData.find((s) => s.staffId === selectedStaff?.staffId);
			setExclusions(entry?.exclusions || []);
			setToast(translations[language].calendar.exclusion.toast.removeError);
			setTimeout(() => setToast(""), 800);
		} finally {
			setIsDeleting(false);
		}
	};
	function formatTimeDisplay(time) {
		if (!time) return "";

		const [h, m] = time.split(":").map(Number);
		const hour12 = h % 12 || 12;
		const minutes = String(m).padStart(2, "0");
		const period = h >= 12 ? "PM" : "AM";

		return `${hour12}:${minutes} ${period}`;
	}

	return (
		<div className="sf-excl-overlay" onClick={onClose}>
			<div className="sf-excl-modal" onClick={(e) => e.stopPropagation()}>
				<div className="sf-excl-header">
					<h3>{translations[language].calendar.exclusion.title}</h3>
					<button className="sf-excl-close" onClick={onClose}>
						✕
					</button>
				</div>

				{/* STAFF PICKER */}
				<div className="sf-sdd-div">
					{staffList.length > 1 && (
						<StaffDropdown
							staffList={staffList}
							selectedStaffId={selectedStaff?.staffId || ""}
							onSelect={(id) => {
								const found = staffList.find((s) => s.staffId === id);
								if (found) {
									setSelectedStaff(found);
									setSelectedScheduleId(found.scheduleId);
								}
								setStaffDropdownOpen(false);
							}}
							language={language}
							open={staffDropdownOpen}
							onToggle={() => setStaffDropdownOpen((p) => !p)}
                            showAnyOption={false}
						/>
					)}
				</div>

				<div className="sf-excl-body">
					{/* DATE RANGE */}
					<div className="sf-excl-box">
						<label>{translations[language].calendar.exclusion.startDate}</label>

						<div className="sf-date-trigger" onClick={() => setShowStartCalendar((prev) => !prev)}>
							{dayjs(startDate).format("MMM D, YYYY")}
						</div>

						{showStartCalendar && (
							<div className="sf-calendar-popover">
								<DayPicker
									mode="single"
									selected={dayjs(startDate).toDate()}
									onSelect={(date) => {
										if (!date) return;

										if (isFullyBlocked(date) || isPast(date)) return;

										const newStart = dayjs(date).format("YYYY-MM-DD");

										setStartDate(newStart);

										// 🔥 Auto-fix end date if now invalid
										if (dayjs(newStart).isAfter(dayjs(endDate))) {
											setEndDate(newStart);
										}

										setShowStartCalendar(false);
									}}
									modifiers={{
										past: (date) => isPast(date),
										blocked: (date) => isFullyBlocked(date),
										partial: (date) => isPartiallyBlocked(date),
									}}
									modifiersClassNames={{
										past: "sf-day-past",
										blocked: "sf-day-blocked",
										partial: "sf-day-partial",
									}}
								/>
							</div>
						)}
					</div>

					<div className="sf-excl-box">
						<label>{translations[language].calendar.exclusion.endDate}</label>

						<div className="sf-date-trigger" onClick={() => setShowEndCalendar((prev) => !prev)}>
							{dayjs(endDate).format("MMM D, YYYY")}
						</div>

						{showEndCalendar && (
							<div className="sf-calendar-popover">
								<DayPicker
									mode="single"
									selected={dayjs(endDate).toDate()}
									onSelect={(date) => {
										if (!date) return;

										// Cannot pick before start
										if (dayjs(date).isBefore(dayjs(startDate))) return;

										// Cannot pick blocked
										if (isFullyBlocked(date)) return;

										setEndDate(dayjs(date).format("YYYY-MM-DD"));
										setShowEndCalendar(false);
									}}
									modifiers={{
										past: (date) => isPast(date),
										blocked: (date) => isFullyBlocked(date),
										beforeStart: (date) => dayjs(date).isBefore(dayjs(startDate)),
									}}
									modifiersClassNames={{
										past: "sf-day-past",
										blocked: "sf-day-blocked",
										beforeStart: "sf-day-disabled",
									}}
								/>
							</div>
						)}
					</div>

					{/* FULL DAY CHECKBOX */}
					<div className="sf-excl-box">
						<label className="sf-excl-checkbox-label">
							<input type="checkbox" checked={fullDay} onChange={() => setFullDay(!fullDay)} />
							&nbsp; {translations[language].calendar.exclusion.fullDay}
						</label>

						{!fullDay && isSingleDay && (
							<div className="sf-excl-time-range">
								<div>
									<label>{translations[language].calendar.exclusion.startTime}</label>
									<select
										value={startTime}
										onChange={(e) => {
											const newStart = e.target.value;

											setStartTime(newStart);

											// find next available slot
											const nextSlot = times.find((t) => t > newStart);

											setEndTime(nextSlot || "");
										}}
									>
										<option value="">{language === "es" ? "Inicio" : "Start"}</option>
										{times.map((t) => (
											<option
												key={t}
												value={t}
												disabled={getBlockedRangesForDate(startDate).some((b) => {
													const ts = dayjs(t, "HH:mm");
													const bs = dayjs(b.start, "HH:mm");
													const be = dayjs(b.end, "HH:mm");
													return !ts.isBefore(bs) && ts.isBefore(be);
												})}
											>
												{formatTimeDisplay(t)}
											</option>
										))}
									</select>
								</div>
								<div>
									<label>{translations[language].calendar.exclusion.endTime}</label>
									<select value={endTime} onChange={(e) => setEndTime(e.target.value)} disabled={!startTime}>
										<option value="">{language === "es" ? "Fin" : "End"}</option>
										{times
											.filter((t) => !startTime || t > startTime)

											.map((t) => (
												<option key={t} value={t} disabled={timeConflicts(startDate, startTime, t)}>
													{formatTimeDisplay(t)}
												</option>
											))}
									</select>
								</div>
							</div>
						)}
					</div>

					{/* REASON */}
					<div className="sf-excl-box">
						<label>{translations[language].calendar.exclusion.reasonLabel}</label>
						<input
							type="text"
							placeholder={translations[language].calendar.exclusion.reasonPlaceholder}
							value={reason}
							onChange={(e) => setReason(e.target.value)}
						/>
					</div>

					{/* EXISTING EXCLUSIONS */}
					{exclusions.length > 0 && (
						<div className="sf-excl-section">
							<h4>{translations[language].calendar.exclusion.existing}</h4>

							<div className="sf-excl-list">
								{exclusions.map((ex, i) => {
									const isFullDay = ex.blocks?.length === 0 || (ex.blocks?.[0]?.start === "00:00" && ex.blocks?.[0]?.end === "23:59");

									return (
										<div key={i} className="sf-excl-item">
											<div className="sf-excl-item-left">
												<div className="sf-excl-item-date">
													{dayjs(ex.startDate).format("MMM D")} — {dayjs(ex.endDate).format("MMM D")}
												</div>

												<div className="sf-excl-item-range">
													{isFullDay
														? translations[language].calendar.exclusion.fullDayLabel
														: ex.blocks?.map((b) => `${formatTimeDisplay(b.start)} – ${formatTimeDisplay(b.end)}`).join(", ")}
												</div>

												{ex.reason && <div className="sf-excl-item-reason">{ex.reason}</div>}
											</div>

											<button className="sf-excl-delete-btn" onClick={() => handleDelete(i)}>
												<Trash2 size={18} />
											</button>
										</div>
									);
								})}
							</div>
						</div>
					)}
					{bookingConflicts.length > 0 && (
						<>
							<div className="sf-conflicts-list">
								{bookingConflicts.map((b) => (
									<div key={b.bookingId || `${b.dateStr}-${b.start}`} className="sf-conflict-item">
										<div className="sf-conflict-left">
											<div className="sf-conflict-client">{b.clientName || (language === "es" ? "Reserva" : "Booking")}</div>

											<div className="sf-conflict-time">
												{dayjs(b.start).format("MMM D")} • {dayjs(b.start).format("h:mm A")} – {dayjs(b.end).format("h:mm A")}
											</div>
										</div>

										{!!onManageBooking && (
											<button
												type="button"
												className="sf-conflict-manage"
												onClick={() =>
													onManageBooking({
														bookingId: b.bookingId,
														dateStr: b.dateStr,
														staffId: b.staffId,
													})
												}
											>
												{language === "es" ? "Gestionar" : "Manage"}
											</button>
										)}
									</div>
								))}
							</div>
						</>
					)}
				</div>

				<div className="sf-base-footer">
					{banner.message && <BannerMessageFixed text={banner.message} type={banner.type} />}
					<button className="btn ghost" onClick={onClose}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="btn" onClick={saveExclusion} disabled={saving}>
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ExclusionModal;
